import { useQuery } from "@tanstack/react-query";

import { convertObjectToCamelCase } from "@/helpers";
import {
  IAHostConversationProps,
  IAHostConversationListWithPagesProps,
} from "@/types";

import { QUERIES_ADMIN } from "./api.constants";
import { api } from "./api.service";

import {
  AI_HOST_CONVERSATIONS_URL,
  AI_HOST_CONVERSATION_URL,
} from "./api.urls";

export const useHostConversationList = ({
  params,
  isEnabled = true,
}: {
  params?: Record<string, string | number>;
  isEnabled?: boolean;
}) => {
  const getConversationList = async (): Promise<{
    success: boolean;
    message: string;
    data?: IAHostConversationListWithPagesProps;
  }> => {
    try {
      const copyParams = { ...params };

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          };
        }

        return acc;
      }, {});

      const { data } = await api.get(AI_HOST_CONVERSATIONS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
        },
      });

      return {
        success: true,
        message: "Conversations fetched successfully!",
        data: convertObjectToCamelCase(
          data
        ) as IAHostConversationListWithPagesProps,
      };
    } catch (err) {
      return {
        success: false,
        message: "Failed to get conversations!",
        data: undefined,
      };
    }
  };

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_HOST_CONVERSATION.module, params],
    queryFn: () => getConversationList(),
    enabled: isEnabled,
  });
};

export const useHostConversationDetail = (
  uuid?: string,
  configs?: {
    refetchInterval?: number;
  }
) => {
  const getConversationDetail = async (): Promise<{
    success: boolean;
    message: string;
    data?: IAHostConversationProps;
  }> => {
    try {
      const { data } = await api.get(AI_HOST_CONVERSATION_URL(uuid));

      return {
        success: true,
        message: "Conversation fetched successfully!",
        data: convertObjectToCamelCase(data) as IAHostConversationProps,
      };
    } catch (err) {
      return {
        success: false,
        message: "Failed to get conversation!",
        data: undefined,
      };
    }
  };

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_HOST_CONVERSATION.module, uuid],
    queryFn: () => getConversationDetail(),
    enabled: !!uuid,
    ...configs,
  });
};
